export const deviceSchemaKeys = {
	all: ['deviceSchema'] as const,
	/** One project's state-key definitions (derived states folded in). */
	stateKeys: (tenantId: string) => [...deviceSchemaKeys.all, 'stateKeys', tenantId] as const,
	/** Prefix over every metadata-key entry, for invalidation after a create/rename/delete. */
	allMetadataKeys: () => [...deviceSchemaKeys.all, 'metadataKeys'] as const,
	/** One project's metadata-key definitions. Impersonation is part of the key: it changes the
	 *  keys the endpoint returns, so the two views must not share an entry. */
	metadataKeys: (tenantId: string, impersonateAdmin: boolean) =>
		[...deviceSchemaKeys.allMetadataKeys(), tenantId, impersonateAdmin] as const,
}

export const actionKeys = {
	all: ['actions'] as const,
	/** Exact-id lookup of one device inside an action's device list (progress-log device dropdown search).
	 *  `tenantId` is '' at project scope. */
	deviceSearch: (actionId: number, tenantId: string, term: string) =>
		[...actionKeys.all, 'deviceSearch', actionId, tenantId, term] as const,
	/** Progress-log rows for one (action, device). `tenantId` is '' at project scope. */
	progressLogs: (actionId: number, deviceId: string, tenantId: string) =>
		[...actionKeys.all, 'progressLogs', actionId, deviceId, tenantId] as const,
	/** One action's payload and settings. `tenantId` is '' at project scope. `includeFileInfo` is part
	 *  of the key because it changes the response shape — it merges checksum/size/url into `payload`. */
	payload: (actionId: number, tenantId: string, includeFileInfo: boolean) =>
		[...actionKeys.all, 'payload', actionId, tenantId, includeFileInfo] as const,
	/** An action's devices, paged as the progress-log dropdown scrolls. `tenantId` is '' at project scope. */
	devices: (actionId: number, tenantId: string) => [...actionKeys.all, 'devices', actionId, tenantId] as const,
	/** Fleet-wide attaches still queued or in progress — the exclusion source for the attach form's pickers. */
	inflightAttaches: (organizationId: string) => [...actionKeys.all, 'inflightAttaches', organizationId] as const,
}

export const deviceKeys = {
	all: ['devices'] as const,
	/** Prefix matching every filter-options suggestion query, across all scopes — for invalidation. */
	allFilterOptions: () => [...deviceKeys.all, 'filterOptions'] as const,
	/** Base key for filter-options suggestions. `scope` = dataset context (project/org, status, filters). */
	filterOptions: (scope: string) => [...deviceKeys.allFilterOptions(), scope] as const,
	/** Infinite-query key for one column's paginated/searched value suggestions. */
	columnFilterOptions: (scope: string, columnId: string, search: string) =>
		[...deviceKeys.filterOptions(scope), 'column', columnId, search] as const,
	/** Paginated device-state search (Streams page → device_state → View Data). */
	stateSearch: (params: Record<string, unknown>) => [...deviceKeys.all, 'stateSearch', params] as const,
	/** Infinite-query base key for the lazy DeviceDropdown list. `scope` = project/org + status + tenant;
	 *  DeviceDropdown appends the serialized filters. */
	dropdown: (scope: string) => [...deviceKeys.all, 'dropdown', scope] as const,
	/** One derived-state membership query: which of `idsKey`'s devices match `statusKey`. */
	statusMembership: (scope: string, statusKey: string, idsKey: string) =>
		[...deviceKeys.all, 'statusMembership', scope, statusKey, idsKey] as const,
	/** One page of device ids for a device-wise fleet panel (LED/Last Value/Aggregate Value).
	 *  `scope` = tenant + fleet filters + activity window, so panels on one dashboard share a page. */
	fleetPanelDeviceIds: (scope: string, page: number, perPage: number) =>
		[...deviceKeys.all, 'fleetPanelDeviceIds', scope, page, perPage] as const,
	/** Active devices for metadata lookups and device pickers. `scope` = tenant, so every consumer
	 *  on a page (dashboard header, panels, device dropdown) shares one fetch. */
	activeDevices: (scope: string) => [...deviceKeys.all, 'activeDevices', scope] as const,
	/** One page of a Device Pulse bucket's devices. `scope` = tenant + fleet filters + bucket bounds +
	 *  when those bounds were resolved, so paging stays on one frozen window. */
	pulseBucketDevices: (scope: string, page: number, perPage: number) =>
		[...deviceKeys.all, 'pulseBucketDevices', scope, page, perPage] as const,
	/** Device metadata for a panel's configured keys, unpaginated. `scope` = tenant + the key list,
	 *  so panels asking for the same keys share one fetch. */
	metadataForKeys: (scope: string) => [...deviceKeys.all, 'metadataForKeys', scope] as const,
}
